import { useInfiniteQuery } from "@tanstack/react-query";
import { boronClient, PER_PAGE } from "../../../api";
import { createError } from "../../../errors";
import type { paths } from "../../../lib/api/v1";
import {
  hasStudentSearchCondition,
  type IssuableStudent,
  STUDENTS_PATH,
  type StudentSearchConditions,
} from "./types";
import { useSendCommonQueryError } from "./useSendCommonQueryError";

type StudentsQuery = NonNullable<
  paths[typeof STUDENTS_PATH]["get"]["parameters"]["query"]
>;

// 起票する生徒の検索。conditions が null（まだ検索していない）か、条件がひとつもなければ呼ばない（ISS-02）。
// 応答に件数がないため、PER_PAGE 件に満たないページで終わりとみなす。
// searchCount は検索した回数。同じ条件で検索し直したときも取り直すため、キーに含める。
// 401・メンテナンス・緊急停止は既存の共通処理に渡す
export const useSearchIssuableStudents = ({
  sectionId,
  conditions,
  searchCount,
}: {
  sectionId: string;
  conditions: StudentSearchConditions | null;
  searchCount: number;
}) => {
  const result = useInfiniteQuery({
    queryKey: [STUDENTS_PATH, sectionId, conditions, searchCount],
    queryFn: ({ pageParam }) =>
      fetchIssuableStudents({
        sectionId,
        conditions: conditions as StudentSearchConditions,
        page: pageParam,
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) =>
      lastPage.length < PER_PAGE ? undefined : allPages.length + 1,
    enabled: conditions !== null && hasStudentSearchCondition(conditions),
    gcTime: 0,
    refetchOnWindowFocus: false,
    retry: false,
  });
  useSendCommonQueryError(result.error);

  const loadMore = () => {
    if (!result.isFetching && result.hasNextPage) {
      result.fetchNextPage();
    }
  };

  return {
    students: result.data?.pages.flat(),
    isPending: result.isPending,
    isFetchingNextPage: result.isFetchingNextPage,
    error: result.error,
    loadMore,
  };
};

const fetchIssuableStudents = async ({
  sectionId,
  conditions,
  page,
}: {
  sectionId: string;
  conditions: StudentSearchConditions;
  page: number;
}): Promise<IssuableStudent[]> => {
  // 生成型のクエリキーは "tag_ids[]" 形式だが、boronClient の querySerializer がブラケットを付けるため、
  // 実行時はブラケットなしのキーで渡してキャストする（useFetchStudentFollowSummaries と同じ扱い）
  const query = {
    last_name: conditions.lastName.trim() || undefined,
    first_name: conditions.firstName.trim() || undefined,
    job: conditions.job,
    tag_ids: conditions.tagIds,
    smart_tag: conditions.smartTag,
    page,
    per: PER_PAGE,
  } as StudentsQuery;

  const { response, data } = await boronClient.GET(STUDENTS_PATH, {
    params: { path: { section_id: sectionId }, query },
  });
  if (response.ok && data) {
    return data.students;
  }
  throw await createError(response);
};
